import { Institution } from '../../types/Institution';
import { Course } from '../../types/Course';
import { QueryClient } from 'react-query';
import { ensureQuery } from '../../utils/functions/ensureQuery';
import { EstudiApiClient } from '../interfaces/EstudiApiClient/EstudiApiClient';
import { Teacher } from '../../types/Teacher';
import { TeacherInfo } from '../../types/TeacherInfo';
import { institutionsQueryFunction } from './queries/institutionsQueryFunction';
import { coursesQueryFunction } from './queries/coursesQueryFunction';
import { teachersQueryFunction } from './queries/teachersQueryFunction';
import { teacherProfileQueryFunction } from './queries/teacherProfileQueryFunction';
import { CalendarInfo } from '../../types/CalendarInfo';
import { courseQueryFunction } from './queries/courseQueryFunction';
import { AuthApiClient } from '../interfaces/EstudiApiClient/AuthApiClient';
import { UpdateTeacherInfo } from '../../types/UpdateTeacherInfo';
import { TeachersFilters } from '../interfaces/EstudiApiClient/dtos/TeachersFilters';
import { Reservation } from '../../types/Reservation';
import { reservationAdapter } from './mappers/reservation';
import { ApiReservation } from './queries/dtos/ApiReservation';
import { InstitutionTypeFilter } from './queries/institutionsQueryFunction';

const rateLabels: Record<number, string> = {
  0: 'Malo',
  1: 'Regular',
  2: 'Bueno',
  3: 'Muy bueno',
  4: 'Excelente'
};

export class EstudiApiQueryClient implements EstudiApiClient {
  public readonly auth: AuthApiClient;

  private readonly queryClient: QueryClient;

  constructor(queryClient: QueryClient, authClient: AuthApiClient) {
    this.queryClient = queryClient;
    this.auth = authClient;
  }

  public async getInstitutionById(params: { institutionId: string }): Promise<Institution | null> {
    //FixMe: when the API exposes the get by id, change this implementation
    return this.getInstitutions().then(
      (institutions) =>
        institutions.find((institution) => institution.value === params.institutionId) ?? null
    );
  }

  public async getInstitutions(params?: { type?: InstitutionTypeFilter }): Promise<Institution[]> {
    const institutionsQuery = institutionsQueryFunction(params?.type ?? 'university');

    return ensureQuery(this.queryClient, institutionsQuery);
  }

  public async getCourseById(params: { courseId: string }): Promise<Course | null> {
    const courseQuery = courseQueryFunction(params.courseId);

    return ensureQuery(this.queryClient, courseQuery);
  }

  public async getCourses(params?: {
    institutionId?: string;
    type?: InstitutionTypeFilter;
  }): Promise<Course[]> {
    const coursesQuery = coursesQueryFunction(params?.institutionId, params?.type ?? 'university');

    return ensureQuery(this.queryClient, coursesQuery);
  }

  public async getTeachers(params: TeachersFilters): Promise<Teacher[]> {
    const teachersQuery = teachersQueryFunction(params);

    return ensureQuery(this.queryClient, teachersQuery);
  }

  public async getTeacherProfile(params: { teacherId: string }): Promise<TeacherInfo> {
    const teacherProfileQuery = teacherProfileQueryFunction(params.teacherId);

    return ensureQuery(this.queryClient, teacherProfileQuery);
  }

  public async getTeacherCalendar(params: { teacherId: string }): Promise<CalendarInfo> {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ESTUDI_API_URL}/estudi/web/teachers/${params.teacherId}/profile/calendar`
      );

      return await response.json();
    } catch (error: unknown) {
      console.error(error);

      throw error;
    }
  }

  public async updateTeacherProfile(params: {
    teacherId: string;
    profileData: UpdateTeacherInfo;
  }): Promise<void> {
    try {
      const response = await this.auth.fetchWithAuth(
        `${process.env.REACT_APP_ESTUDI_API_URL}/estudi/web/teachers/${params.teacherId}/profile/update`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(params.profileData)
        }
      );

      if (!response.ok) {
        throw new Error(`Error updating teacher profile: ${response.statusText}`);
      }

      await this.queryClient.refetchQueries([params.teacherId, 'teacher-profile']);
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  public async getReservation(params: {
    reservationId: string;
    includeRate?: boolean;
    includeGroup?: boolean;
  }): Promise<Reservation> {
    try {
      const url = new URL(`${process.env.REACT_APP_ESTUDI_API_URL}/estudi/web/reservations`);

      url.searchParams.set('reservationId', params.reservationId);

      if (params.includeRate) url.searchParams.set('includeRate', 'true');
      if (params.includeGroup) url.searchParams.set('includeGroup', 'true');

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(`Reservation not found: ${response.status}`);
      }

      const dto: ApiReservation = await response.json();

      return reservationAdapter(dto);
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }

  public async getReservationHistory(params: {
    studentEmail: string;
    teacherId: string;
  }): Promise<Reservation[]> {
    try {
      const url = new URL(
        `${process.env.REACT_APP_ESTUDI_API_URL}/estudi/web/reservations/history`
      );

      url.searchParams.set('studentEmail', params.studentEmail);
      url.searchParams.set('teacherId', params.teacherId);

      const response = await fetch(url.toString());

      if (!response.ok) {
        return [];
      }

      const dtos: ApiReservation[] = await response.json();

      return dtos.map(reservationAdapter);
    } catch (error: unknown) {
      console.error(error);

      return [];
    }
  }

  public async submitReservationRating(params: {
    reservationId: string;
    stars: number;
    comment: string;
  }): Promise<void> {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ESTUDI_API_URL}/estudi/v1/reservations/external/providers/wpp/rate/notify`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: params.reservationId,
            rate: {
              label: rateLabels[params.stars - 1] ?? 'Regular',
              value: params.stars - 1
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Error submitting rating: ${response.status}`);
      }
    } catch (error: unknown) {
      console.error(error);
      throw error;
    }
  }
}
